// DashboardLayoutModel.js

/**
 * DashboardLayoutModel.js
 *
 * Document-shape helpers for dashboard layout v2 (_dashboards).
 */

import { Timestamp } from 'firebase/firestore';

import { generateKey } from 'utilities/Keys';
import {
  pruneEmptyAncestorContainers,
  removeBlockKeys,
} from 'page/pageEditorLayoutUtils';

export const DASHBOARD_LAYOUT_VERSION = 2;

const DEFAULT_ROOT_NODE_KEY = 'layout_root';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeColumnCount(columnCount) {
  return Number(columnCount) === 2 ? 2 : 3;
}

function getValidLayout(layout) {
  return layout && typeof layout === 'object' && !Array.isArray(layout)
    ? layout
    : null;
}

function getValidBlocksByKey(blocksByKey) {
  return blocksByKey && typeof blocksByKey === 'object' && !Array.isArray(blocksByKey)
    ? blocksByKey
    : {};
}

/**
 * buildDefaultDashboardLayout
 *
 * Returns the empty v2 dashboard layout scaffold.
 *
 * @returns {{ blocksByKey: object, layout: object, layoutVersion: number }}
 */
export function buildDefaultDashboardLayout() {
  const slotKey = generateKey();

  return {
    blocksByKey: {},
    layout: {
      rootNodeKey: DEFAULT_ROOT_NODE_KEY,
      nodes: {
        [DEFAULT_ROOT_NODE_KEY]: {
          key: DEFAULT_ROOT_NODE_KEY,
          kind: 'stack',
          children: [slotKey],
        },
        [slotKey]: {
          key: slotKey,
          kind: 'block_slot',
          blockKey: null,
        },
      },
    },
    layoutVersion: DASHBOARD_LAYOUT_VERSION,
  };
}

/**
 * normalizeDashboardViewBlock
 *
 * Returns a sanitized dashboard VIEW block or null when invalid.
 *
 * @param {unknown} block - Candidate block payload.
 * @param {string} [blockKey=''] - Optional stable block key.
 * @returns {object|null} Normalized block payload.
 */
export function normalizeDashboardViewBlock(block, blockKey = '') {
  const cleanedBlockKey = normalizeString(blockKey || block?.key);
  const viewId = normalizeString(block?.viewId);
  const blockType = normalizeString(block?.type).toUpperCase();

  if (blockType !== 'VIEW' || !viewId) {
    return null;
  }

  const titleOverride = normalizeString(block?.titleOverride) || null;

  return {
    key: cleanedBlockKey || generateKey(),
    type: 'VIEW',
    viewId,
    titleOverride,
    createdAt: block?.createdAt || Timestamp.now(),
    updatedAt: block?.updatedAt || Timestamp.now(),
  };
}

/**
 * normalizeDashboardBlocksByKey
 *
 * Returns sanitized dashboard blocks keyed by block key with unique viewIds.
 *
 * @param {unknown} blocksByKey - Candidate block map.
 * @returns {object} Sanitized blocks keyed by block key.
 */
export function normalizeDashboardBlocksByKey(blocksByKey) {
  const source = getValidBlocksByKey(blocksByKey);
  const seenViewIds = new Set();
  const next = {};

  Object.entries(source).forEach(([rawBlockKey, block]) => {
    const normalizedBlock = normalizeDashboardViewBlock(block, rawBlockKey);
    if (!normalizedBlock) return;
    if (seenViewIds.has(normalizedBlock.viewId)) return;

    seenViewIds.add(normalizedBlock.viewId);
    next[normalizedBlock.key] = normalizedBlock;
  });

  return next;
}

/**
 * collectViewIdsFromBlocks
 *
 * Returns unique view ids referenced by dashboard VIEW blocks.
 *
 * @param {unknown} blocksByKey - Candidate block map.
 * @returns {string[]} Unique view ids.
 */
export function collectViewIdsFromBlocks(blocksByKey) {
  const source = getValidBlocksByKey(blocksByKey);
  const seen = new Set();

  return Object.values(source).reduce((viewIds, block) => {
    if (normalizeString(block?.type).toUpperCase() !== 'VIEW') {
      return viewIds;
    }

    const viewId = normalizeString(block?.viewId);
    if (!viewId || seen.has(viewId)) {
      return viewIds;
    }

    seen.add(viewId);
    viewIds.push(viewId);
    return viewIds;
  }, []);
}

/**
 * migrateDashboardCardsToLayout
 *
 * Converts legacy dashboard cards into a v2 layout tree and block map.
 *
 * @param {object} [options] - Migration source values.
 * @param {array} [options.cards=[]] - Legacy dashboard cards.
 * @param {number} [options.columnCount=3] - Legacy column count.
 * @returns {{ blocksByKey: object, layout: object, layoutVersion: number }}
 */
export function migrateDashboardCardsToLayout({ cards = [], columnCount = 3 } = {}) {
  const normalizedCards = Array.isArray(cards) ? cards : [];
  const normalizedColumnCount = normalizeColumnCount(columnCount);

  if (!normalizedCards.length) {
    return buildDefaultDashboardLayout();
  }

  const blocksByKey = {};
  const nodes = {};
  const rowKey = generateKey();
  const columnStackKeys = [];

  for (let columnIndex = 0; columnIndex < normalizedColumnCount; columnIndex += 1) {
    const stackKey = generateKey();
    const slotKeys = [];

    const columnCards = normalizedCards
      .filter((card) => {
        const cardColumnIndex = Number.isFinite(Number(card?.columnIndex))
          ? Math.max(0, Math.min(normalizedColumnCount - 1, Number(card.columnIndex)))
          : 0;
        return cardColumnIndex === columnIndex;
      })
      .sort((left, right) => {
        const leftPosition = Number.isFinite(Number(left?.position)) ? Number(left.position) : 0;
        const rightPosition = Number.isFinite(Number(right?.position)) ? Number(right.position) : 0;
        return leftPosition - rightPosition;
      });

    if (!columnCards.length) {
      const emptySlotKey = generateKey();
      nodes[emptySlotKey] = {
        key: emptySlotKey,
        kind: 'block_slot',
        blockKey: null,
      };
      slotKeys.push(emptySlotKey);
    } else {
      columnCards.forEach((card) => {
        const viewId = normalizeString(card?.viewId);
        if (!viewId) return;

        const blockKey = normalizeString(card?.key) || generateKey();
        blocksByKey[blockKey] = normalizeDashboardViewBlock({
          type: 'VIEW',
          viewId,
          titleOverride: card?.titleOverride,
          createdAt: card?.createdAt,
          updatedAt: card?.updatedAt,
        }, blockKey);

        const slotKey = generateKey();
        nodes[slotKey] = {
          key: slotKey,
          kind: 'block_slot',
          blockKey,
        };
        slotKeys.push(slotKey);
      });
    }

    nodes[stackKey] = {
      key: stackKey,
      kind: 'stack',
      children: slotKeys,
    };
    columnStackKeys.push(stackKey);
  }

  nodes[rowKey] = {
    key: rowKey,
    kind: 'row',
    children: columnStackKeys,
  };
  nodes[DEFAULT_ROOT_NODE_KEY] = {
    key: DEFAULT_ROOT_NODE_KEY,
    kind: 'stack',
    children: [rowKey],
  };

  return {
    blocksByKey: normalizeDashboardBlocksByKey(blocksByKey),
    layout: {
      rootNodeKey: DEFAULT_ROOT_NODE_KEY,
      nodes,
    },
    layoutVersion: DASHBOARD_LAYOUT_VERSION,
  };
}

/**
 * isDashboardLayoutV2
 *
 * Returns whether a dashboard document is already on layout v2.
 *
 * @param {object|null} dashboard - Dashboard document.
 * @returns {boolean}
 */
export function isDashboardLayoutV2(dashboard) {
  return Number(dashboard?.layoutVersion) === DASHBOARD_LAYOUT_VERSION
    && getValidLayout(dashboard?.layout) != null;
}

/**
 * resolveDashboardLayoutDocument
 *
 * Returns normalized v2 layout fields for a dashboard document.
 *
 * @param {object|null} dashboard - Dashboard document.
 * @returns {{ blocksByKey: object, layout: object, layoutVersion: number }}
 */
export function resolveDashboardLayoutDocument(dashboard) {
  if (isDashboardLayoutV2(dashboard)) {
    return {
      blocksByKey: normalizeDashboardBlocksByKey(dashboard?.blocksByKey),
      layout: getValidLayout(dashboard?.layout),
      layoutVersion: DASHBOARD_LAYOUT_VERSION,
    };
  }

  return migrateDashboardCardsToLayout({
    cards: dashboard?.cards,
    columnCount: dashboard?.columnCount,
  });
}

/**
 * buildDashboardEditorDocument
 *
 * Returns the layout editor payload for a dashboard document.
 *
 * @param {object|null} dashboard - Dashboard document.
 * @returns {object|null} Editor document payload.
 */
export function buildDashboardEditorDocument(dashboard) {
  if (!dashboard?.key) {
    return null;
  }

  const resolvedLayout = resolveDashboardLayoutDocument(dashboard);

  return {
    accessMode: 'PRIVATE',
    blocksByKey: resolvedLayout.blocksByKey,
    key: dashboard.key,
    layout: resolvedLayout.layout,
    status: 'active',
    title: normalizeString(dashboard?.title) || 'Untitled Dashboard',
    baseKey: normalizeString(dashboard?.baseKey) || null,
  };
}

/**
 * removeViewIdFromDashboardLayout
 *
 * Removes a deleted view reference from dashboard layout blocks and slots.
 *
 * @param {object} options - Cleanup options.
 * @param {object} options.layout - Current layout tree.
 * @param {object} options.blocksByKey - Current block map.
 * @param {string} options.viewId - Deleted view id.
 * @returns {{ blocksByKey: object, layout: object, removedBlockKeys: string[] }}
 */
export function removeViewIdFromDashboardLayout({ layout, blocksByKey, viewId } = {}) {
  const cleanedViewId = normalizeString(viewId);
  const resolvedLayout = getValidLayout(layout) || buildDefaultDashboardLayout().layout;
  const resolvedBlocksByKey = getValidBlocksByKey(blocksByKey);

  if (!cleanedViewId) {
    return {
      blocksByKey: resolvedBlocksByKey,
      layout: resolvedLayout,
      removedBlockKeys: [],
    };
  }

  const removedBlockKeys = Object.entries(resolvedBlocksByKey)
    .filter(([, block]) => normalizeString(block?.viewId) === cleanedViewId)
    .map(([blockKey]) => blockKey);

  if (!removedBlockKeys.length) {
    return {
      blocksByKey: resolvedBlocksByKey,
      layout: resolvedLayout,
      removedBlockKeys: [],
    };
  }

  let nextLayout = { ...resolvedLayout, nodes: { ...resolvedLayout.nodes } };
  const nextBlocksByKey = removeBlockKeys(resolvedBlocksByKey, removedBlockKeys);
  const slotKeysToPrune = [];

  removedBlockKeys.forEach((blockKey) => {
    Object.values(nextLayout.nodes).forEach((node) => {
      if (node?.kind !== 'block_slot' || normalizeString(node?.blockKey) !== blockKey) {
        return;
      }

      slotKeysToPrune.push(node.key);
      nextLayout.nodes[node.key] = {
        ...node,
        blockKey: null,
      };
    });
  });

  slotKeysToPrune.forEach((slotKey) => {
    nextLayout = pruneEmptyAncestorContainers(nextLayout, slotKey);
  });

  return {
    blocksByKey: nextBlocksByKey,
    layout: nextLayout,
    removedBlockKeys,
  };
}

/**
 * buildDashboardLayoutPersistencePayload
 *
 * Returns sanitized v2 layout fields for dashboard writes.
 *
 * @param {object} options - Persistence payload values.
 * @param {object} options.layout - Layout tree to persist.
 * @param {object} options.blocksByKey - Block map to persist.
 * @returns {{ blocksByKey: object, layout: object, layoutVersion: number }}
 */
export function buildDashboardLayoutPersistencePayload({ layout, blocksByKey } = {}) {
  return {
    blocksByKey: normalizeDashboardBlocksByKey(blocksByKey),
    layout: getValidLayout(layout) || buildDefaultDashboardLayout().layout,
    layoutVersion: DASHBOARD_LAYOUT_VERSION,
  };
}
